<template>
  <div>
    <div
      :class="[
        $style.videoBanner,
        spacingStateClass,
        { 'mediabanner-clickable': isClickable },
        { [$style.headerBanner]: isHeaderBanner },
      ]"
      @click="handleVideoClick"
    >
      <video
        ref="videoPlayer"
        autoplay
        loop
        :muted="isMuted"
        playsinline
        :aria-label="mediaItem.altText || 'Video content'"
        tabindex="0"
        role="region"
        :style="aspectRatio"
      >
        Your browser does not support the video tag.
      </video>
      <div
        v-if="isUploadedVideo || isMuxVideo"
        :class="[$style.overlaySvgs, 'mediabanner-button-overlay']"
      >
        <IconDescription
          v-if="mediaItem.showAudioDescription"
          :aria-label="'Toggle audio description for ' + (mediaItem.altText || 'video')"
          tabindex="0"
          @click.stop="toggleAudioDescription"
        ></IconDescription>
        <component
          :is="isPlaying ? IconPause : IconPlay"
          v-if="finalShowPlayButton"
          ref="playButton"
          :aria-label="isPlaying ? 'Pause video' : 'Play video'"
          tabindex="1"
          @click.stop="togglePlayPause"
          @keydown.stop="activateTogglePlayPauseByKeyBoard"
        ></component>
        <component
          :is="isMuted ? IconSoundOff : IconSoundOn"
          v-if="finalShowEnableSound"
          ref="muteButton"
          :aria-label="isMuted ? 'Unmute video' : 'Mute video'"
          tabindex="2"
          @click.stop="toggleMute"
          @keydown.stop="activateToggleMuteByKeyBoard"
        ></component>
        <IconLinkNew
          v-if="finalShowVideoLink && finalLinkUrl"
          ref="linkButton"
          :aria-label="mediaItem.caption ? 'Learn more about ' + mediaItem.caption : 'Learn more'"
          tabindex="3"
          @click.stop="linkClicked(finalLinkUrl)"
          @keydown.stop="activateLinkClickedByKeyBoard"
        ></IconLinkNew>
      </div>
    </div>
    <div v-if="isModalOpen" :class="$style.modal" @click="closeModal">
      <IconExit
        :class="$style.iconExit"
        tabindex="0"
        aria-label="Close video modal"
        @click.stop="closeModal"
      ></IconExit>
      <div :class="$style.modalContent" @click.stop>
        <video
          ref="modalVideoPlayer"
          autoplay
          controls
          :muted="isMuted"
          playsinline
          :aria-label="mediaItem.altText || 'Full video content'"
          tabindex="0"
          role="region"
        >
          Your browser does not support the video tag.
        </video>
      </div>
    </div>
    <div class="mediabanner-caption">
      <a :href="mediaItem.captionLink || null" :target="mediaItem.captionLink ? '_self' : null">
        <span
          class="captionText"
          :class="[{ column: spacingState == 'Column' }, { withLink: captionLinkExists }]"
          v-html="mediaItem.caption"
        ></span>
        <span v-if="captionLinkExists" class="captionLink"><b>Learn More →</b></span>
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  defineProps,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useCssModule,
  watch,
} from 'vue';
import {
  globalAudioMuted,
  useAnalytics,
  useHandleError,
  useSpacingState,
} from '@/composables/Common.js';
import Hls from 'hls.js';
import IconDescription from '@/assets/svgs/media/description.svg?component';
import IconExit from '@/assets/svgs/media/x.svg?component';
import IconLinkNew from '@/assets/svgs/media/newlink.svg?component';
import IconPause from '@/assets/svgs/media/pause.svg?component';
import IconPlay from '@/assets/svgs/media/play.svg?component';
import IconSoundOff from '@/assets/svgs/media/soundOff.svg?component';
import IconSoundOn from '@/assets/svgs/media/soundOn.svg?component';
import { MediaItem } from '@/types/mediaBanner.interface';

const $style = useCssModule();

const { ga4Event } = useAnalytics();
const { handleError } = useHandleError();

const props = withDefaults(
  defineProps<{
    aspectRatio?: string;
    isActive?: boolean;
    isHeaderBanner: boolean;
    linkUrl?: string;
    mediaItem: MediaItem;
    showEnableSound?: boolean;
    showLinkButton?: boolean;
    showPlayButton?: boolean;
    spacingState?: string;
  }>(),
  {
    aspectRatio: undefined,
    showEnableSound: undefined,
    showLinkButton: undefined,
    showPlayButton: undefined,
    spacingState: 'Full Screen',
  },
);

const fullVideoSource = ref('');
const hls = ref<Hls | null>(null);
const isAudioDescriptionPlaying = ref(false);
const isPlaying = ref(true);
const isModalOpen = ref(false);
const isMuted = ref(true);
const linkButton = ref<SVGElement | null>(null);
const modalHls = ref<Hls | null>(null);
const modalVideoPlayer = ref<HTMLVideoElement | null>(null);
const muteButton = ref<SVGElement | null>(null);
const originalVideoSource = ref('');
const playButton = ref<SVGElement | null>(null);
const videoPlayer = ref<HTMLVideoElement | null>(null);

const aspectRatio = computed(() => {
  return { aspectRatio: props.aspectRatio ?? 'unset' };
});
const captionLinkExists = computed(() => {
  return props.mediaItem.captionLink != null && props.mediaItem.captionLink != '';
});
const finalLinkUrl = computed(() => {
  return props.linkUrl !== undefined ? props.linkUrl : props.mediaItem.linksTo;
});
const finalShowEnableSound = computed(() => {
  return props.showEnableSound !== undefined ? props.showEnableSound : props.mediaItem.enableSound;
});
const finalShowPlayButton = computed(() => {
  return props.showPlayButton !== undefined
    ? props.showPlayButton
    : props.mediaItem.enablePlayPause;
});
const finalShowVideoLink = computed(() => {
  return props.showLinkButton !== undefined ? props.showLinkButton : props.mediaItem.enableLink;
});
const isClickable = computed(() => {
  return !!finalLinkUrl.value || (!!fullVideoSource.value && !!videoPlayer.value);
});
const isMuxVideo = computed(() => {
  return !!props.mediaItem.muxShortVideoId;
});
const isUploadedVideo = computed(() => {
  return !!props.mediaItem.videoFile;
});
const muxShortIdToUse = computed(() => {
  const isMobile = window.innerWidth <= 768;

  if (isMobile && props.mediaItem.muxVideoMobileId) {
    return props.mediaItem.muxVideoMobileId;
  }

  return props.mediaItem.muxShortVideoId;
});

const spacingStateClass = useSpacingState(props, $style);

onBeforeUnmount(() => {
  if (hls.value) {
    hls.value.destroy();
    hls.value = null;
  }
  if (modalHls.value) {
    modalHls.value.destroy();
    modalHls.value = null;
  }
});
onMounted(() => {
  nextTick(() => {
    initializeVideo();

    if (videoPlayer.value) {
      videoPlayer.value.addEventListener('play', () => {
        isPlaying.value = true;
      });
      videoPlayer.value.addEventListener('pause', () => {
        isPlaying.value = false;
      });
    }
  });
});

watch(
  () => props.isActive,
  (state) => {
    if (globalAudioMuted.value) {
      isMuted.value = true;
    } else {
      isMuted.value = false;
    }

    if (!state && isPlaying.value) {
      togglePlayPause();
    }

    if (state && !isPlaying.value) {
      togglePlayPause();
    }
  },
);

function activateLinkClickedByKeyBoard(event: KeyboardEvent): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    linkClicked('');
    setTimeout(() => {
      linkButton.value.$el.focus();
    }, 100);
  }
}

function activateToggleMuteByKeyBoard(event: KeyboardEvent): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    toggleMute();
    setTimeout(() => {
      muteButton.value.$el.focus();
    }, 100);
  }
}

function activateTogglePlayPauseByKeyBoard(event: KeyboardEvent): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    togglePlayPause();
    setTimeout(() => {
      playButton.value.$el.focus();
    }, 100);
  }
}

function closeModal(): void {
  if (modalVideoPlayer.value) {
    modalVideoPlayer.value.pause();
  }

  isModalOpen.value = false;

  if (modalHls.value) {
    modalHls.value.destroy();
    modalHls.value = null;
  }

  if (videoPlayer.value) {
    videoPlayer.value.currentTime = 0;
    videoPlayer.value.play();
  }
}

function getFullVideoSource(): string {
  if (props.mediaItem.muxLongVideoId) {
    return `https://stream.mux.com/${props.mediaItem.muxLongVideoId}.m3u8?min_resolution=1080p`;
  }

  return '';
}

function getVideoSource(): string {
  if (isUploadedVideo.value && isUploadedVideo.value != '') {
    return props.mediaItem.videoFile;
  }

  if (isMuxVideo.value) {
    return `https://stream.mux.com/${muxShortIdToUse.value}.m3u8?min_resolution=1080p`;
  }

  return '';
}

// GUGG: save for uploaded video implementation
function getVideoType(): string {
  if (isUploadedVideo.value) {
    return 'video/mp4';
  }

  if (isMuxVideo.value) {
    return 'application/vnd.apple.mpegurl';
  }

  return '';
}

function handleVideoClick(): void {
  if (fullVideoSource.value && videoPlayer.value) {
    videoPlayer.value.pause();
    isModalOpen.value = true;
    ga4Event({
      click_type: 'video_banner_open_modal',
      component: 'video_banner',
      content_group: 'media',
      link_text: `Opened video modal: ${props.mediaItem.altText || 'No Alt Text'}`,
    });
    nextTick(() => {
      if (modalVideoPlayer.value) {
        modalVideoPlayer.value.muted = isMuted.value;

        setupVideoSource(fullVideoSource.value, modalVideoPlayer.value, modalHls);

        modalVideoPlayer.value.currentTime = 0;
        modalVideoPlayer.value.play();
      }
    });
  }
}

function initializeVideo(): void {
  if (!videoPlayer.value) {
    return;
  }

  try {
    if (hls.value) {
      hls.value.destroy();
      hls.value = null;
    }

    originalVideoSource.value = getVideoSource();
    fullVideoSource.value = getFullVideoSource();
    videoPlayer.value.muted = isMuted.value;

    setupVideoSource(originalVideoSource.value, videoPlayer.value, hls);

    isPlaying.value = true;
    videoPlayer.value.play();
  } catch (err) {
    handleError('VideoBanner', `Error in "initializeVideo": ${err}`, false);
  }
}

function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function linkClicked(url: string): void {
  if (url) {
    ga4Event({
      click_type: 'video_banner_link_click',
      component: 'video_banner',
      content_group: 'media',
      link_text: `Link clicked: ${url}`,
    });
    window.location.href = url;
  }
}

function setupVideoSource(source, player, hlsInstance): void {
  if (player) {
    if (hlsInstance.value) {
      hlsInstance.value.destroy();
      hlsInstance.value = null;
    }

    if (isIOS()) {
      player.src = source;
    } else if (Hls.isSupported()) {
      hlsInstance.value = new Hls();
      hlsInstance.value.loadSource(source);
      hlsInstance.value.attachMedia(player);
      hlsInstance.value.on(Hls.Events.ERROR, (event, data) => {
        handleError('VideoBanner', `HLS.js error: ${data}`, false);
      });
    } else {
      handleError('VideoBanner', 'This browser does not support HLS or MSE', false);
    }
  }
}

function setVoiceAndSpeak(
  utterance: SpeechSynthesisUtterance,
  voices: SpeechSynthesisVoice[],
): void {
  const preferredVoices = [
    'Google US English',
    'Microsoft Zira Desktop - English (United States)',
    'Alex', // macOS voice
  ];
  let selectedVoice = voices.find((voice) => preferredVoices.includes(voice.name));

  if (!selectedVoice) {
    const englishVoices = voices.filter((voice) => voice.lang.startsWith('en'));

    selectedVoice = englishVoices[0];
  }

  if (selectedVoice) {
    utterance.voice = selectedVoice;
  }

  window.speechSynthesis.speak(utterance);
}

function toggleAudioDescription(): void {
  if (props.mediaItem && props.mediaItem.audioDescription) {
    const text = props.mediaItem.audioDescription;

    if ('speechSynthesis' in window) {
      if (isAudioDescriptionPlaying.value) {
        window.speechSynthesis.cancel();
        isAudioDescriptionPlaying.value = false;
      } else {
        if (window.speechSynthesis.speaking) {
          window.speechSynthesis.cancel();
        }

        const utterance = new SpeechSynthesisUtterance(text);

        utterance.lang = 'en-US';
        utterance.pitch = 1;
        utterance.rate = 0.8;
        utterance.volume = 1;

        utterance.onend = () => {
          isAudioDescriptionPlaying.value = false;
        };

        let voices = window.speechSynthesis.getVoices();

        if (voices.length === 0) {
          window.speechSynthesis.onvoiceschanged = () => {
            voices = window.speechSynthesis.getVoices();
            setVoiceAndSpeak(utterance, voices);
          };
        } else {
          setVoiceAndSpeak(utterance, voices);
        }

        isAudioDescriptionPlaying.value = true;
      }
    }
  }
}

function toggleMute(): void {
  if (!isMuted.value) {
    isMuted.value = true;
    globalAudioMuted.value = true;
  } else {
    if (!globalAudioMuted.value) {
      return;
    }

    isMuted.value = false;
    globalAudioMuted.value = false;
  }

  if (videoPlayer.value) {
    videoPlayer.value.muted = isMuted.value;
  }

  if (modalVideoPlayer.value) {
    modalVideoPlayer.value.muted = isMuted.value;
  }

  ga4Event({
    click_type: isMuted.value ? 'video_mute' : 'video_unmute',
    component: 'video_banner',
    content_group: 'media',
    link_text: `Video ${isMuted.value ? 'muted' : 'unmuted'}: ${props.mediaItem.altText || 'No Alt Text'}`,
  });
}

function togglePlayPause(): void {
  if (videoPlayer.value) {
    if (isPlaying.value) {
      videoPlayer.value.pause();
      ga4Event({
        click_type: 'video_pause',
        component: 'video_banner',
        content_group: 'media',
        link_text: `Paused video: ${props.mediaItem.altText || 'No Alt Text'}`,
      });
    } else {
      videoPlayer.value.play();
      ga4Event({
        click_type: 'video_play',
        component: 'video_banner',
        content_group: 'media',
        link_text: `Played video: ${props.mediaItem.altText || 'No Alt Text'}`,
      });
    }

    isPlaying.value = !isPlaying.value;
  }
}
</script>

<style lang="scss">
@use '@/styles/banner.module.scss' as *;
</style>

<style lang="scss" module>
.videoBanner {
  position: relative;
  width: 100%;
  height: 100%;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &.fullScreen {
    @include full-bleed-styles;

    & {
      height: 100%;
    }
  }

  &.headerBanner {
    @include full-bleed-styles;

    & {
      height: calc(100dvh - $mobile-header-menu-height);
    }
  }

  .overlaySvgs {
    bottom: 1rem;
    gap: 0.5rem;
    left: 1rem;

    svg {
      height: 44px;
      width: 44px;
    }
  }

  * {
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  }
}

.modal {
  display: flex;
  background-color: black;
  height: 100dvh;
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 1000;

  .iconExit {
    cursor: pointer;
    box-sizing: border-box;
    opacity: 0.7;
    overflow: visible;
    position: absolute;
    transition: opacity 0.3s;
    z-index: 100;
    right: 0.5rem;
    top: 0.5rem;
    width: 44px;
    height: 44px;

    svg {
      fill: white;
      height: 100%;
      width: 100%;
    }

    &:hover {
      opacity: 1;
    }
  }

  .modalContent {
    align-self: center;
    box-sizing: border-box;
    margin: 0 auto;
    position: relative;
    width: 100%;
    padding-left: 1rem;
    padding-right: 1rem;

    video {
      display: block;
      margin: 0 auto;
      object-fit: cover;
      height: auto;
      width: 100%;
    }
  }

  * {
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  }
}

@media (min-width: $break-point) {
  .videoBanner {
    &.fullScreen {
      @include full-bleed-styles;

      & {
        height: calc(100vh - $desktop-header-menu-height);
      }
    }
    &.column {
      max-width: $content-max-width;
    }
  }

  .modal {
    .iconExit {
      height: 40px;
      right: 1rem;
      top: 1rem;
      width: 40px;
    }

    .modalContent {
      padding-left: 0;
      padding-right: 0;

      video {
        height: calc(100svh - 7rem);
        margin-top: 3.5rem;
        width: calc(100% - 7rem);
      }
    }
  }
}
</style>
